/**
 * TypeScript mirrors of the API DTOs (`backend/src/Sazerac.Application/Contracts`).
 *
 * Shared by the website and the dashboard so both speak the same contract. The API
 * serialises enums as strings, which is why these are string unions.
 */

// ------------------------------------------------------------------- ENUMS

export type ReservationStatus =
  | 'Pending' | 'Confirmed' | 'Seated' | 'Completed' | 'Cancelled' | 'NoShow' | 'Waitlisted';

export type ReservationSource = 'Website' | 'Phone' | 'WalkIn' | 'Staff';
export type SmokingPreference = 'NonSmoking' | 'SmokingArea';
export type MenuType = 'Drinks' | 'Food';
export type DrinkTemperature = 'NotApplicable' | 'Hot' | 'Cold';
export type MediaVisibility = 'Public' | 'Private';
export type ContentStatus = 'Draft' | 'Published';

export type ConciergeRequestStatus =
  | 'Submitted' | 'InReview' | 'QuoteSent' | 'QuoteAccepted' | 'QuoteDeclined'
  | 'Confirmed' | 'InProgress' | 'Completed' | 'Cancelled';

export type ConciergeQuoteStatus = 'Draft' | 'Sent' | 'Accepted' | 'Declined' | 'Expired' | 'Withdrawn';

export type LaundryOrderStatus =
  | 'Requested' | 'PickupScheduled' | 'Collected' | 'Cleaning'
  | 'QualityChecked' | 'OutForDelivery' | 'Delivered' | 'Cancelled';

export type LaundryPricingModel = 'PerItem' | 'PerKilogram' | 'Included';
export type EventEnquiryStatus = 'New' | 'Contacted' | 'ProposalSent' | 'Confirmed' | 'Lost' | 'Cancelled';
export type ContactEnquiryStatus = 'New' | 'InProgress' | 'Answered' | 'Closed' | 'Spam';
export type NotificationChannel = 'Email' | 'Sms' | 'WhatsApp';
export type NotificationStatus = 'Queued' | 'Sending' | 'Sent' | 'Failed' | 'SimulatedOnly' | 'Cancelled';
export type DepositStatus = 'NotRequired' | 'Pending' | 'Paid' | 'Refunded' | 'Failed' | 'SimulatedOnly';
export type WaitlistStatus = 'Waiting' | 'Offered' | 'Converted' | 'Expired' | 'Cancelled';

export type DayName =
  | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

// -------------------------------------------------------------- SITE/CONTENT

export interface Media {
  id: string;
  url: string;
  altText?: string | null;
  caption?: string | null;
  width?: number | null;
  height?: number | null;
  contentType: string;
  sizeBytes: number;
  visibility: MediaVisibility;
  fileName: string;
  createdAtUtc: string;
}

export interface OpeningHour {
  dayOfWeek: DayName;
  day: string;
  opens?: string | null;
  closes?: string | null;
  closesAfterMidnight: boolean;
  isClosed: boolean;
}

export interface Closure {
  id: string;
  startDate: string;
  endDate: string;
  startTime?: string | null;
  endTime?: string | null;
  isFullDay: boolean;
  reason: string;
  blocksReservations: boolean;
}

export interface SeatingArea {
  id: string;
  name: string;
  code: string;
  description?: string | null;
  isOutdoor: boolean;
  allowsSmoking: boolean;
  displayOrder: number;
}

export interface Location {
  id: string;
  name: string;
  slug: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  postalCode?: string | null;
  country: string;
  phone?: string | null;
  whatsAppNumber?: string | null;
  email?: string | null;
  timeZoneId: string;
  currency: string;
  latitude?: number | null;
  longitude?: number | null;
  mapEmbedUrl?: string | null;
  directionsNote?: string | null;
  displayOrder: number;
  openingHours: OpeningHour[];
  seatingAreas: SeatingArea[];
  upcomingClosures: Closure[];
}

export interface SiteSettings {
  businessName: string;
  defaultCurrency: string;
  defaultLocale: string;
  supportEmail?: string | null;
  supportPhone?: string | null;
  instagramUrl?: string | null;
  facebookUrl?: string | null;
  reservationsEnabled: boolean;
  conciergeEnabled: boolean;
  laundryEnabled: boolean;
  newsletterEnabled: boolean;
  /** True while the site still runs on seeded demo content. */
  showDemoContentNotice: boolean;
  locations: Location[];
}

/** One block in a CMS page document. `data` is block-type specific. */
export interface ContentBlock<T = Record<string, unknown>> {
  id: string;
  type: string;
  visible: boolean;
  data: T;
}

export interface ContentPage {
  id: string;
  key: string;
  title: string;
  description?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  ogImage?: Media | null;
  noIndex: boolean;
  /** JSON string holding a ContentBlock[]; parse with parseBlocks(). */
  bodyJson: string;
  status: ContentStatus;
  hasUnpublishedChanges: boolean;
  publishedAtUtc?: string | null;
  updatedAtUtc?: string | null;
  /** Media referenced by blocks in bodyJson, keyed by media id. */
  media: Record<string, Media>;
}

export function parseBlocks(bodyJson: string | null | undefined): ContentBlock[] {
  if (!bodyJson) return [];
  try {
    const parsed = JSON.parse(bodyJson);
    return Array.isArray(parsed) ? (parsed as ContentBlock[]).filter((b) => b.visible !== false) : [];
  } catch {
    return [];
  }
}

export interface MenuItem {
  id: string;
  menuCategoryId: string;
  categoryName: string;
  menuType: MenuType;
  name: string;
  slug: string;
  description?: string | null;
  ingredients?: string | null;
  price: number;
  currency: string;
  image?: Media | null;
  isAlcoholic: boolean;
  temperature: DrinkTemperature;
  tasteTags: string[];
  dietaryTags: string[];
  allergens: string[];
  isAvailable: boolean;
  isFeatured: boolean;
  isPublished: boolean;
  displayOrder: number;
}

export interface MenuCategory {
  id: string;
  name: string;
  slug: string;
  menuType: MenuType;
  description?: string | null;
  displayOrder: number;
  isPublished: boolean;
  items: MenuItem[];
}

export interface MenuItemDetail {
  item: MenuItem;
  categorySlug: string;
  categoryDescription?: string | null;
  related: MenuItem[];
}

export interface Experience {
  id: string;
  title: string;
  slug: string;
  scheduleLabel: string;
  description?: string | null;
  included?: string | null;
  priceInfo?: string | null;
  dressCode?: string | null;
  image?: Media | null;
  showOnHome: boolean;
  isPublished: boolean;
  displayOrder: number;
}

export interface EventListing {
  id: string;
  title: string;
  slug: string;
  summary?: string | null;
  description?: string | null;
  startUtc: string;
  endUtc?: string | null;
  locationName?: string | null;
  image?: Media | null;
  ticketInfo?: string | null;
  bookingUrl?: string | null;
  isPublished: boolean;
  displayOrder: number;
}

export interface GalleryItem {
  id: string;
  title: string;
  category: string;
  credit?: string | null;
  image?: Media | null;
  showOnHome: boolean;
  isPublished: boolean;
  displayOrder: number;
}

export interface Faq {
  id: string;
  question: string;
  answer: string;
  category?: string | null;
  isPublished: boolean;
  displayOrder: number;
}

export interface Testimonial {
  id: string;
  quote: string;
  author: string;
  sourceNote?: string | null;
  isPublished: boolean;
  displayOrder: number;
}

// ------------------------------------------------------------- RESERVATIONS

export interface AvailabilitySlot {
  time: string;
  startUtc: string;
  endUtc: string;
  isAvailable: boolean;
  tablesRemaining: number;
  preferredAreaFull: boolean;
  reason?: string | null;
}

export interface AvailabilityDay {
  locationId: string;
  date: string;
  timeZoneId: string;
  isOpen: boolean;
  unavailableReason?: string | null;
  unavailableMessage?: string | null;
  durationMinutes: number;
  slots: AvailabilitySlot[];
  seatingAreas: SeatingArea[];
  minPartySize: number;
  maxPartySize: number;
  waitlistAvailable: boolean;
  depositEnabled: boolean;
  depositPerPerson: number;
  currency: string;
}

export interface DepositInfo {
  amount: number;
  currency: string;
  status: DepositStatus;
  providerName: string;
  /** True when no real payment provider is configured and nothing was charged. */
  simulated: boolean;
  redirectUrl?: string | null;
  message?: string | null;
}

export interface CreateReservationRequest {
  locationId: string;
  date: string;
  time: string;
  partySize: number;
  preferredSeatingAreaId?: string | null;
  smokingPreference: SmokingPreference;
  guestName: string;
  email: string;
  phone: string;
  occasion?: string | null;
  specialRequests?: string | null;
  acceptedTerms: boolean;
  requestDeposit: boolean;
  marketingConsent: boolean;
}

export interface ReservationCreated {
  reference: string;
  status: ReservationStatus;
  statusLabel: string;
  startUtc: string;
  endUtc: string;
  date: string;
  time: string;
  partySize: number;
  locationName: string;
  seatingPreference?: string | null;
  manageToken: string;
  manageTokenExpiresUtc: string;
  requiresStaffApproval: boolean;
  deposit?: DepositInfo | null;
  notices: string[];
}

export interface ReservationHistoryEntry {
  status: string;
  changedAtUtc: string;
  changedBy: string;
  note?: string | null;
}

export interface GuestReservation {
  reference: string;
  status: ReservationStatus;
  statusLabel: string;
  date: string;
  time: string;
  startUtc: string;
  endUtc: string;
  partySize: number;
  guestName: string;
  email: string;
  phone?: string | null;
  locationName: string;
  locationAddress: string;
  timeZoneId: string;
  locationId: string;
  preferredSeatingAreaId?: string | null;
  seatingPreference?: string | null;
  seatingPreferenceNote: string;
  occasion?: string | null;
  specialRequests?: string | null;
  smokingPreference: SmokingPreference;
  canModify: boolean;
  canCancel: boolean;
  cannotModifyReason?: string | null;
  deposit?: DepositInfo | null;
  history: ReservationHistoryEntry[];
}

export interface AssignedTable {
  id: string;
  name: string;
  seatingAreaName: string;
  maxCapacity: number;
  assignedAutomatically: boolean;
}

export interface AdminReservation {
  id: string;
  reference: string;
  status: ReservationStatus;
  statusLabel: string;
  source: ReservationSource;
  date: string;
  time: string;
  startUtc: string;
  endUtc: string;
  partySize: number;
  guestId: string;
  guestName: string;
  email: string;
  phone?: string | null;
  guestIsVip: boolean;
  locationId: string;
  locationName: string;
  preferredSeatingAreaId?: string | null;
  seatingPreference?: string | null;
  tables: AssignedTable[];
  occasion?: string | null;
  specialRequests?: string | null;
  internalNotes?: string | null;
  smokingPreference: SmokingPreference;
  deposit?: DepositInfo | null;
  createdAtUtc: string;
  history: ReservationHistoryEntry[];
}

// ---------------------------------------------------------------- SERVICES

export interface ConciergeService {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  pricingNote?: string | null;
  requiresQuote: boolean;
  leadTimeHours: number;
  displayOrder: number;
}

export interface StatusHistoryEntry {
  status: string;
  changedAtUtc: string;
  changedBy: string;
  note?: string | null;
}

export interface ConciergeQuote {
  id: string;
  amount: number;
  currency: string;
  summary?: string | null;
  termsNote?: string | null;
  status: ConciergeQuoteStatus;
  statusLabel: string;
  sentAtUtc?: string | null;
  expiresAtUtc?: string | null;
  respondedAtUtc?: string | null;
  isActionable: boolean;
}

export interface ConciergeNote {
  id: string;
  body: string;
  authorName: string;
  createdAtUtc: string;
}

export interface ConciergeRequest {
  id: string;
  reference: string;
  serviceName: string;
  status: ConciergeRequestStatus;
  statusLabel: string;
  /** False while the request is still only an enquiry. */
  isConfirmedService: boolean;
  preferredDate?: string | null;
  preferredTime?: string | null;
  details: string;
  guestName: string;
  email: string;
  phone?: string | null;
  createdAtUtc: string;
  activeQuote?: ConciergeQuote | null;
  history: StatusHistoryEntry[];
  hasAttachment: boolean;
}

export interface AdminConciergeRequest extends ConciergeRequest {
  guestId: string;
  assignedStaffId?: string | null;
  assignedStaffName?: string | null;
  attachmentMediaId?: string | null;
  quotes: ConciergeQuote[];
  notes: ConciergeNote[];
  reservationReference?: string | null;
}

export interface ConciergeRequestSubmitted {
  reference: string;
  status: ConciergeRequestStatus;
  statusLabel: string;
  statusNote: string;
  trackingToken: string;
  trackingTokenExpiresUtc: string;
}

export interface LaundryService {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  pricingModel: LaundryPricingModel;
  basePrice: number;
  minimumCharge: number;
  currency: string;
  priceLabel: string;
  standardTurnaroundHours: number;
  supportsExpress: boolean;
  expressTurnaroundHours: number;
  expressSurchargePercent: number;
  displayOrder: number;
}

export interface LaundryGarmentCategory {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  typicalWeightKg: number;
  isDelicate: boolean;
  displayOrder: number;
  unitPrice?: number | null;
}

export interface LaundryQuoteLine {
  garmentCategoryId?: string | null;
  description?: string | null;
  quantity: number;
  weightKg?: number | null;
}

export interface LaundryQuoteLineResult {
  description: string;
  quantity: number;
  weightKg?: number | null;
  unitPrice: number;
  lineTotal: number;
}

export interface LaundryQuote {
  subtotal: number;
  expressSurcharge: number;
  total: number;
  minimumChargeApplied: number;
  currency: string;
  isEstimate: boolean;
  estimateNote: string;
  lines: LaundryQuoteLineResult[];
}

export interface LaundryTimelineStep {
  status: LaundryOrderStatus;
  label: string;
  isComplete: boolean;
  isCurrent: boolean;
  reachedAtUtc?: string | null;
}

export interface LaundryOrderItem {
  id: string;
  garmentCategoryId?: string | null;
  description: string;
  quantity: number;
  weightKg?: number | null;
  unitPrice: number;
  lineTotal: number;
  isConfirmed: boolean;
}

export interface LaundryOrder {
  id: string;
  reference: string;
  serviceName: string;
  status: LaundryOrderStatus;
  statusLabel: string;
  guestName: string;
  email: string;
  phone?: string | null;
  pickupAddress: string;
  pickupAtUtc: string;
  deliveryAtUtc?: string | null;
  isExpress: boolean;
  careInstructions?: string | null;
  garmentSummary?: string | null;
  estimatedTotal: number;
  finalTotal?: number | null;
  measuredWeightKg?: number | null;
  currency: string;
  requiresGuestPriceApproval: boolean;
  priceApprovedAtUtc?: string | null;
  priceNote: string;
  createdAtUtc: string;
  items: LaundryOrderItem[];
  history: StatusHistoryEntry[];
  timeline: LaundryTimelineStep[];
}

export interface AdminLaundryOrder extends LaundryOrder {
  guestId: string;
  assignedStaffId?: string | null;
  assignedStaffName?: string | null;
  internalNotes?: string | null;
  estimatedSubtotal: number;
  estimatedExpressSurcharge: number;
}

export interface LaundryOrderSubmitted {
  reference: string;
  status: LaundryOrderStatus;
  statusLabel: string;
  estimate: LaundryQuote;
  trackingToken: string;
  trackingTokenExpiresUtc: string;
}

// ---------------------------------------------------------------- ENQUIRIES

export interface EventEnquiry {
  id: string;
  reference: string;
  eventType: string;
  preferredDate?: string | null;
  alternativeDate?: string | null;
  guestCount: number;
  budgetRange?: string | null;
  foodBeverageRequirements?: string | null;
  entertainmentRequirements?: string | null;
  additionalNotes?: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone?: string | null;
  companyName?: string | null;
  status: EventEnquiryStatus;
  statusLabel: string;
  assignedStaffId?: string | null;
  assignedStaffName?: string | null;
  internalNotes?: string | null;
  estimatedValue?: number | null;
  currency?: string | null;
  hasAttachment: boolean;
  createdAtUtc: string;
}

export interface ContactEnquiry {
  id: string;
  reference: string;
  department: string;
  name: string;
  email: string;
  phone?: string | null;
  message: string;
  status: ContactEnquiryStatus;
  statusLabel: string;
  assignedStaffId?: string | null;
  assignedStaffName?: string | null;
  internalNotes?: string | null;
  respondedAtUtc?: string | null;
  createdAtUtc: string;
}

// ---------------------------------------------------------------- DASHBOARD

export interface CountByLabel {
  label: string;
  count: number;
}

export interface ReservationsToday {
  total: number;
  pending: number;
  confirmed: number;
  seated: number;
  completed: number;
  cancelled: number;
  noShow: number;
  waitlisted: number;
  expectedGuests: number;
  seatedGuests: number;
  tablesInService: number;
  tablesTotal: number;
}

export interface Workload {
  open: number;
  newToday: number;
  awaitingAction: number;
  dueToday: number;
  overdue: number;
  byStatus: CountByLabel[];
}

export interface EnquiryWorkload {
  eventsNew: number;
  eventsOpen: number;
  contactNew: number;
  contactOpen: number;
}

export interface NotificationHealth {
  queued: number;
  failed: number;
  sentLast24h: number;
  /** Written to the development preview log only — not delivered. */
  simulatedLast24h: number;
  emailProviderConfigured: boolean;
  emailProviderName: string;
}

export interface ActivityItem {
  occurredAtUtc: string;
  type: string;
  title: string;
  detail?: string | null;
  reference?: string | null;
  entityId?: string | null;
}

export interface UpcomingReservation {
  id: string;
  reference: string;
  time: string;
  startUtc: string;
  guestName: string;
  partySize: number;
  status: string;
  tables?: string | null;
  occasion?: string | null;
  isVip: boolean;
}

export interface DashboardOverview {
  date: string;
  timeZoneId: string;
  locationId?: string | null;
  locationName?: string | null;
  reservationsToday: ReservationsToday;
  concierge: Workload;
  laundry: Workload;
  enquiries: EnquiryWorkload;
  notifications: NotificationHealth;
  recentActivity: ActivityItem[];
  nextArrivals: UpcomingReservation[];
}

export interface OperationsReport {
  from: string;
  to: string;
  currency: string;
  reservationsCreated: number;
  reservationsCompleted: number;
  reservationsCancelled: number;
  reservationsNoShow: number;
  coversSeated: number;
  averagePartySize: number;
  reservationsByDay: CountByLabel[];
  reservationsBySeatingArea: CountByLabel[];
  reservationsByOccasion: CountByLabel[];
  conciergeRequests: number;
  conciergeCompleted: number;
  conciergeAcceptedQuoteValue: number;
  conciergeQuotesSent: number;
  laundryOrders: number;
  laundryDelivered: number;
  laundryEstimatedValue: number;
  laundryConfirmedFinalValue: number;
  eventEnquiries: number;
  eventEnquiriesConfirmed: number;
  eventEstimatedPipelineValue: number;
  depositsRecordedPaid: number;
  depositsSimulated: number;
  moneyDisclaimer: string;
}

// ------------------------------------------------------------------- ADMIN

export interface StaffProfile {
  id: string;
  fullName: string;
  email: string;
  jobTitle?: string | null;
  roles: string[];
  capabilities: Record<string, boolean>;
  mustChangePassword: boolean;
  lastLoginUtc?: string | null;
  defaultLocationId?: string | null;
}

export interface AuthResult {
  accessToken: string;
  expiresAtUtc: string;
  refreshToken: string;
  user: StaffProfile;
}

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/** RFC 7807 problem response shape returned by the API on any error. */
export interface ApiProblem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  errors?: Record<string, string[]>;
  traceId?: string;
}
